import { Channel } from '@/Enums/Channel.enum';
import Color from '@/Enums/Color.enum';
import GeoType from '@/Enums/Geography/GeographyType.enum';
import type { ITargetingLocation } from '@/Interfaces/Location.interface';
import { CHANNELS, ESTIMATION_CARD_BACKGROUND, isGamChannel } from '@/constants/forecaster';
import { MAP_SCALES } from '@/constants/geo';
import { mapValueForChannel } from '@/helpers/forecaster';
import { getLocationsByType } from '@/helpers/forecaster/estimation';
import { useAppSelector } from '@/store/hooks';
import { selectVisibleForecasterChannels } from '@/store/selectors/currentWhitelabel.selectors';
import { downloadCSV } from '@/store/slices/forecasterSlice';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, IconButton, Menu, MenuItem, Paper, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactNode, memo, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

interface IProps {
  value: ReactNode;
  tabName: string;
  sx?: SxProps<Theme>;
  currentChannel: Channel;
}

const downloadMenuItems = [
  { name: 'All States', geoType: MAP_SCALES.STATE },
  { name: 'All Markets', geoType: MAP_SCALES.DMA },
  { name: 'Selected States', geoType: MAP_SCALES.STATE, isSelected: true },
  { name: 'Selected Markets', geoType: MAP_SCALES.DMA, isSelected: true },
  { name: 'Selected ZIP Codes', geoType: MAP_SCALES.ZIP, isSelected: true },
];

const Impressions = memo(({ value, tabName, sx, currentChannel }: IProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const forecasterChannelsVisibleToWhitelabel = useAppSelector(selectVisibleForecasterChannels);
  const { getValues } = useFormContext();

  const dispatch = useDispatch();

  const open = Boolean(anchorEl);
  const isGam = isGamChannel(currentChannel);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = useCallback(
    (_event: React.MouseEvent<HTMLLIElement>, channel: Channel, geoType: GeoType, isSelected: boolean | undefined) => {
      let geographic: ITargetingLocation[] = [];
      if (isSelected) {
        geographic = getLocationsByType(getValues('geographic'), geoType);
        if (geographic.length === 0) return;
      }

      const filteredChannel = mapValueForChannel(getValues(channel));

      const body = {
        ...filteredChannel,
        name: channel,
        groupBy: geoType,
        daysInRange: getValues('daysInRange'),
        geographic,
      };

      const frequencyCapPerDay = getValues('frequencyCapPerDay');

      if (frequencyCapPerDay) {
        body.frequencyCapPerDay = frequencyCapPerDay;
      }

      dispatch(downloadCSV(body));

      setAnchorEl(null);
    },
    [getValues, dispatch],
  );

  const menuItems = useMemo(
    () =>
      downloadMenuItems.map(item => (
        <MenuItem
          key={item.name}
          onClick={event => handleMenuItemClick(event, currentChannel, item.geoType as GeoType, item.isSelected)}
        >
          {item.name}
        </MenuItem>
      )),
    [handleMenuItemClick, currentChannel],
  );

  const channel = useMemo(() => {
    return CHANNELS.find(
      channel =>
        forecasterChannelsVisibleToWhitelabel.some(vc => vc.name === channel.id) && channel.id === currentChannel,
    );
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
      }}
    >
      {!channel?.hidden && !isGam ? (
        <div className='flex-shrink-0 print:hidden'>
          <IconButton
            aria-label='download'
            size='small'
            onClick={handleClick}
            sx={{
              padding: '2px',
              border: '1px solid rgb(230, 230, 230)',
              position: 'absolute',
              top: '35%',
              right: '20px',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          >
            <FileDownloadOutlined titleAccess='download' fontSize='inherit' />
          </IconButton>
          <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
            {menuItems}
          </Menu>
        </div>
      ) : null}
      <Paper
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingY: '8px',
          background: ESTIMATION_CARD_BACKGROUND,
          ...sx,
        }}
      >
        <Typography component='p' sx={{ fontWeight: 500, fontSize: '12px', color: Color.LightGreen3 }}>
          Projected {tabName} Impressions
        </Typography>
        <Typography component='p' sx={{ fontWeight: 700, fontSize: '30px', color: 'white' }}>
          {value}
        </Typography>
        {isGam ? (
          <Typography component='p' sx={{ fontWeight: 400, fontSize: '11px', color: Color.LightGreen3 }}>
            Native Google Ad Manager availability
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
});

export default Impressions;
